import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AppFrame, Card, Character, PrimaryButton, ProgressBar } from '../components/ui';
import { CharacterMoment } from '../components/characterMoments';
import { syncCertificateClaims } from '../services/certificateSyncApi';
import {
  CERTIFICATE_CATALOG,
  CERTIFICATE_SETS,
  certificateById,
  claimedCertificateCount,
  loadCertificateClaims,
  saveCertificateClaims,
  setComplete,
  type CertificateClaimState,
  type CertificateCharacter,
  type CertificateDefinition,
} from '../domain/certificateCollection';
import { calculateGoalProgress, formatDateKo } from '../domain/rules';
import { useReaction } from '../reactions/ReactionProvider';
import { useAppStore } from '../store/AppStore';

const A = '/assets';

function characterAsset(character: CertificateCharacter): string {
  const assets: Record<CertificateCharacter, string> = {
    'haneul-record': `${A}/characters/haneul/haneul_report_v1.png`,
    'haneul-saving': `${A}/characters/haneul/haneul_piggy_v1.png`,
    'haneul-think': `${A}/characters/haneul/haneul_think_v1.png`,
    'haneul-heart': `${A}/characters/haneul/haneul_heart_v1.png`,
    'haneul-run': `${A}/characters/haneul/haneul_run_v1.png`,
    'haneul-openarms': `${A}/characters/haneul/haneul_openarms_v1.png`,
    'haneul-celebrate': `${A}/characters/haneul/haneul_celebrate_v1.png`,
    'haneul-star': `${A}/characters/haneul/haneul_star_v1.png`,
    'sage-research': `${A}/characters/sage/sage_research_v1.png`,
    'sage-binocular': `${A}/characters/sage/sage_binocular_v1.png`,
    'sage-checklist': `${A}/characters/sage/sage_checklist_v1.png`,
    'arctic-fox': `${A}/characters/friends/arctic_fox_v1.png`,
  };
  return assets[character];
}



const HONOR_HALL_CHARACTER_CAST = [
  { src: '/assets/characters/haneul/haneul_wave_v1.png', label: '첫 인사', role: '호기심' },
  { src: '/assets/characters/haneul/haneul_report_v1.png', label: '기록가', role: '관찰' },
  { src: '/assets/characters/haneul/haneul_think_v1.png', label: '생각가', role: '선택' },
  { src: '/assets/characters/haneul/haneul_piggy_v1.png', label: '저축가', role: '목표' },
  { src: '/assets/characters/haneul/haneul_run_v1.png', label: '탐험가', role: '행동' },
  { src: '/assets/characters/haneul/haneul_openarms_v1.png', label: '보관가', role: '기억' },
  { src: '/assets/characters/haneul/haneul_heart_v1.png', label: '응원가', role: '연결' },
  { src: '/assets/characters/haneul/haneul_jump_v1.png', label: '도전자', role: '도전' },
  { src: '/assets/characters/haneul/haneul_peace_v1.png', label: '평화로운 선택', role: '균형' },
  { src: '/assets/characters/haneul/haneul_thumbs_v1.png', label: '확신의 선택', role: '통제감' },
  { src: '/assets/characters/haneul/haneul_surprise_v1.png', label: '발견자', role: '발견' },
  { src: '/assets/characters/haneul/haneul_celebrate_v1.png', label: '축하대장', role: '성취' },
  { src: '/assets/characters/haneul/haneul_star_v1.png', label: '별빛 수료자', role: '자부심' },
  { src: '/assets/characters/sage/sage_research_v1.png', label: '경제 연구 설옹', role: '이해' },
  { src: '/assets/characters/sage/sage_binocular_v1.png', label: '관찰 설옹', role: '비교' },
  { src: '/assets/characters/sage/sage_calculator_v1.png', label: '계산 설옹', role: '판단' },
  { src: '/assets/characters/sage/sage_checklist_v1.png', label: '인증 설옹', role: '회고' },
  { src: '/assets/characters/friends/arctic_fox_v1.png', label: '북극여우', role: '탐험 힌트' },
] as const;

function rarityLabel(certificate: CertificateDefinition): string {
  return `${certificate.rarity} · ${certificate.category}`;
}

function useCertificateClaimStore(childId: string) {
  const [claims, setClaims] = useState<CertificateClaimState>(() => loadCertificateClaims(childId));
  useEffect(() => setClaims(loadCertificateClaims(childId)), [childId]);
  // Claims used to live only in this browser, so a new device started empty.
  // Syncing widens the local set from the server union and never shrinks it.
  useEffect(() => {
    let active = true;
    void syncCertificateClaims(childId).then((result) => { if (active && result.synced) setClaims(result.claims); });
    return () => { active = false; };
  }, [childId]);
  const save = (next: CertificateClaimState) => {
    setClaims(next);
    saveCertificateClaims(childId, next);
  };
  return { claims, save };
}

export function CertificateCollectionScreen() {
  const { state, patchState } = useAppStore();
  const { emit } = useReaction();
  const claimStore = useCertificateClaimStore(state?.child.id ?? 'unknown');
  if (!state) return <AppFrame title="스노우볼 명예의 전당" navigation="child"><Card>불러오는 중...</Card></AppFrame>;

  const claimedCount = claimedCertificateCount(claimStore.claims);
  const unlockedCount = CERTIFICATE_CATALOG.filter((certificate) => certificate.test(state)).length;
  const totalReward = CERTIFICATE_CATALOG.reduce((sum, certificate) => sum + (claimStore.claims.claims[certificate.id] ? certificate.rewardXp : 0), 0)
    + CERTIFICATE_SETS.reduce((sum, set) => sum + (claimStore.claims.setRewards[set.id] ? set.rewardXp : 0), 0);

  const claimSetReward = async (setId: string) => {
    const set = CERTIFICATE_SETS.find((item) => item.id === setId);
    if (!set || !setComplete(set, claimStore.claims) || claimStore.claims.setRewards[set.id]) return;
    await patchState({ xp: state.xp + set.rewardXp, badges: Array.from(new Set([...state.badges, set.frameName])) });
    const next = { ...claimStore.claims, setRewards: { ...claimStore.claims.setRewards, [set.id]: new Date().toISOString() } };
    claimStore.save(next);
    await emit('badge.unlocked');
  };

  return (
    <AppFrame title="스노우볼 명예의 전당" backTo="/child/me" navigation="child" screenClass="honor-hall-screen">
      <section className="honor-hall-hero">
        <div className="honor-hall-copy">
          <p className="eyebrow">MISSION CERTIFICATE COLLECTION</p>
          <h1>해낸 미션이<br />나만의 명예의 전당이 돼요</h1>
          <p>수료증은 점수표가 아니라 내가 어떤 선택을 반복했는지 보여주는 성장 기록입니다.</p>
          <div className="honor-hall-metrics">
            <span><small>보관 완료</small><strong>{claimedCount} / {CERTIFICATE_CATALOG.length}</strong></span>
            <span><small>발급 가능</small><strong>{unlockedCount - claimedCount}개</strong></span>
            <span><small>수집 보너스</small><strong>{totalReward} XP</strong></span>
          </div>
          <ProgressBar value={(claimedCount / CERTIFICATE_CATALOG.length) * 100} />
        </div>
        <div className="honor-hall-characters" aria-label="명예의 전당을 안내하는 캐릭터들">
          <Character src={`${A}/characters/haneul/haneul_celebrate_v1.png`} alt="수료증 컬렉션을 축하하는 하늘이" />
          <Character src={`${A}/characters/friends/arctic_fox_v1.png`} alt="배지 보드를 안내하는 북극여우" />
          <Character src={`${A}/characters/sage/sage_checklist_v1.png`} alt="수료 기록을 확인하는 설옹" />
        </div>
      </section>

      <CharacterMoment
        src={`${A}/characters/sage/sage_checklist_v1.png`}
        alt="수료증 컬렉션의 의미를 설명하는 설옹"
        eyebrow="설옹의 명예의 전당 안내"
        title="같은 돈을 모아도, 어떤 선택을 했는지는 모두 달라요"
        copy="기록·기다림·비교·가족 대화처럼 내가 키운 힘을 수료증과 배지로 하나씩 보관해 보세요."
        tone="gold"
        compact
      />


      <section className="character-cast-section">
        <div className="section-title-row"><div><span>CHARACTER MOMENT ALBUM</span><h2>성장 순간 캐릭터 도감</h2></div><small>수료증을 모을수록 새로운 표정과 역할이 컬러로 깨어나요</small></div>
        <div className="character-cast-scroll" role="list" aria-label="MRM 공식 캐릭터 성장 순간 도감">
          {HONOR_HALL_CHARACTER_CAST.map((item, index) => {
            const revealed = index < Math.max(4, claimedCount + 4);
            return <article key={item.src} className={`character-cast-card ${revealed ? 'revealed' : 'locked'}`} role="listitem">
              <Character src={item.src} alt={revealed ? `${item.label} 캐릭터` : ''} />
              <strong>{revealed ? item.label : '잠긴 순간'}</strong><small>{revealed ? item.role : `수료증 ${Math.max(1,index - 3)}개 필요`}</small>
            </article>;
          })}
        </div>
      </section>

      <section className="certificate-set-section">
        <div className="section-title-row"><div><span>COLLECTION SET</span><h2>주제별 컬렉션 프레임</h2></div><small>세트 완성 시 특별 프레임과 XP 지급</small></div>
        <div className="certificate-set-grid">
          {CERTIFICATE_SETS.map((set) => {
            const progress = set.certificateIds.filter((id) => Boolean(claimStore.claims.claims[id])).length;
            const complete = progress === set.certificateIds.length;
            const rewarded = Boolean(claimStore.claims.setRewards[set.id]);
            return (
              <article key={set.id} className={`certificate-set-card set-${set.id} ${complete ? 'complete' : ''}`}>
                <div className="set-card-medal"><img src={`${A}/certificates/mrm_cert_02_goldmedal_v01.png`} alt="" /><strong>{progress}/{set.certificateIds.length}</strong></div>
                <div className="set-card-copy"><small>{set.frameName}</small><h3>{set.title}</h3><p>{set.description}</p><div className="set-progress-dots">{set.certificateIds.map((id) => <i key={id} className={claimStore.claims.claims[id] ? 'filled' : ''} />)}</div></div>
                {complete
                  ? <button type="button" className={rewarded ? 'set-reward claimed' : 'set-reward'} disabled={rewarded} onClick={() => void claimSetReward(set.id)}>{rewarded ? '프레임 보관 완료' : `세트 보너스 +${set.rewardXp} XP`}</button>
                  : <span className="set-reward locked">수료증 {set.certificateIds.length - progress}개 더 필요</span>}
              </article>
            );
          })}
        </div>
      </section>

      <section className="certificate-gallery-section">
        <div className="section-title-row"><div><span>CERTIFICATE GALLERY</span><h2>미션 수료증 갤러리</h2></div><small>잠긴 수료증에서도 다음 해제 조건을 확인할 수 있어요</small></div>
        <div className="certificate-collection-grid">
          {CERTIFICATE_CATALOG.map((certificate) => {
            const unlocked = certificate.test(state);
            const claimedAt = claimStore.claims.claims[certificate.id];
            const status = claimedAt ? 'claimed' : unlocked ? 'ready' : 'locked';
            return (
              <Link key={certificate.id} to={`/child/certificates/${certificate.id}`} className={`certificate-collection-card tone-${certificate.tone} ${status}`}>
                <div className="certificate-card-preview">
                  <span className="certificate-card-rarity">{certificate.rarity}</span>
                  <img className="certificate-card-badge" src={certificate.badgeAsset} alt="" />
                  <Character src={characterAsset(certificate.character)} alt="" className="certificate-card-character" />
                  <div className="certificate-card-seal">{String(certificate.order).padStart(2,'0')}</div>
                </div>
                <div className="certificate-card-info">
                  <small>{certificate.category}</small>
                  <h3>{certificate.shortTitle}</h3>
                  <p>{status === 'claimed' ? `${formatDateKo(claimedAt)} 보관` : status === 'ready' ? `지금 발급 가능 · +${certificate.rewardXp} XP` : certificate.unlockRule}</p>
                  <strong>{status === 'claimed' ? '수료증 보기' : status === 'ready' ? '발급받기' : '해제 조건 보기'} <b>›</b></strong>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="certificate-badge-board-section">
        <div className="section-title-row"><div><span>SNOW BADGE BOARD</span><h2>수료 배지 보드</h2></div><small>수료증을 발급받으면 배지가 눈꽃 보드에 꽂혀요</small></div>
        <div className="certificate-badge-board">
          <div className="badge-board-center"><img src={`${A}/certificates/mrm_cert_03_completionseal_v01.png`} alt="MY real Money 공식 인증 인장" /><strong>{claimedCount}</strong><small>COLLECTED</small></div>
          {CERTIFICATE_CATALOG.map((certificate, index) => {
            const claimed = Boolean(claimStore.claims.claims[certificate.id]);
            const unlocked = certificate.test(state);
            return (
              <Link key={certificate.id} to={`/child/certificates/${certificate.id}`} className={`badge-board-slot slot-${index + 1} ${claimed ? 'claimed' : unlocked ? 'ready' : 'locked'}`} aria-label={`${certificate.shortTitle} ${claimed ? '수집 완료' : unlocked ? '발급 가능' : '잠김'}`}>
                <img src={certificate.badgeAsset} alt="" /><span>{index + 1}</span><small>{certificate.shortTitle}</small>
              </Link>
            );
          })}
        </div>
      </section>
    </AppFrame>
  );
}

export function MissionCertificateScreen() {
  const { certificateId } = useParams();
  const { state, patchState } = useAppStore();
  const { emit } = useReaction();
  const claimStore = useCertificateClaimStore(state?.child.id ?? 'unknown');
  const certificate = certificateById(certificateId);
  if (!state || !certificate) return <AppFrame title="수료증" navigation="child"><Card><h2>수료증을 찾지 못했어요</h2><Link className="primary-button link-button" to="/child/hall-of-honor">명예의 전당으로 돌아가기</Link></Card></AppFrame>;

  const unlocked = certificate.test(state);
  const claimedAt = claimStore.claims.claims[certificate.id];
  const issueDate = claimedAt ?? new Date().toISOString();
  const progress = calculateGoalProgress(state.goal);
  const certificateCode = `MRM-${new Date(issueDate).getFullYear()}-${String(certificate.order).padStart(2,'0')}-${state.child.id.slice(-3).toUpperCase()}`;
  const achievementStats = [
    { label: '돈 기록', value: `${state.records.length}회`, icon: `${A}/ui-icons/mrm_ui_01a_record_v01.png` },
    { label: '성장 미션', value: `${state.missions.filter((mission) => mission.status === 'approved').length + state.familyMissionChallenges.filter((mission) => ['submitted','approved'].includes(mission.status)).length}회`, icon: `${A}/ui-icons/mrm_ui_12a_mission_v01.png` },
    { label: '성장 연속', value: `${state.growthSummary.currentStreak}일`, icon: `${A}/badges/mrm_badge_02_missionrewardstar_v01.png` },
    { label: '목표 성장', value: `${progress}%`, icon: `${A}/ui-icons/mrm_ui_12c_saving_v01.png` },
  ];

  const claimCertificate = async () => {
    if (!unlocked || claimedAt) return;
    await patchState({
      xp: state.xp + certificate.rewardXp,
      badges: Array.from(new Set([...state.badges, certificate.shortTitle])),
    });
    const next = { ...claimStore.claims, claims: { ...claimStore.claims.claims, [certificate.id]: new Date().toISOString() } };
    claimStore.save(next);
    await emit('badge.unlocked', { message: `${certificate.shortTitle} 수료증을 명예의 전당에 보관했어요!` });
  };

  return (
    <AppFrame title={certificate.shortTitle} backTo="/child/hall-of-honor" navigation="child" screenClass={`certificate-screen certificate-detail tone-${certificate.tone}`}>
      {!unlocked && (
        <section className="certificate-lock-banner">
          <Character src={`${A}/characters/sage/sage_checklist_v1.png`} alt="잠금 해제 조건을 안내하는 설옹" />
          <div><span>아직 잠긴 수료증</span><h2>{certificate.unlockRule}</h2><p>조건을 달성하면 수료증과 전용 배지, {certificate.rewardXp} XP를 받을 수 있어요.</p></div>
        </section>
      )}
      {unlocked && !claimedAt && (
        <section className="certificate-ready-banner">
          <Character src={`${A}/characters/haneul/haneul_surprise_v1.png`} alt="새 수료증을 발견한 하늘이" />
          <div><span>새 수료증 발견!</span><h2>명예의 전당에 보관할 준비가 됐어요</h2><p>최초 발급 시 전용 배지와 {certificate.rewardXp} XP가 지급됩니다.</p></div>
          <PrimaryButton onClick={() => void claimCertificate()}>수료증 발급받기</PrimaryButton>
        </section>
      )}
      <section className={`certificate-stage ${!unlocked ? 'locked-preview' : ''}`}>
        <article className={`certificate-sheet certificate-variant-${certificate.tone}`} role="document" aria-label={`${state.child.nickname}의 ${certificate.title}`}>
          {!claimedAt && <div className="certificate-preview-watermark">{unlocked ? 'READY TO ISSUE' : 'LOCKED PREVIEW'}</div>}
          <span className="certificate-corner corner-a" aria-hidden="true" /><span className="certificate-corner corner-b" aria-hidden="true" /><span className="certificate-corner corner-c" aria-hidden="true" /><span className="certificate-corner corner-d" aria-hidden="true" />
          <header className="certificate-official-header">
            <div className="certificate-brand-lockup"><span>MY</span><div><strong>MY real Money</strong><small>MISSION CERTIFICATE COLLECTION</small></div></div>
            <img className="certificate-medal" src={certificate.badgeAsset} alt={`${certificate.shortTitle} 배지`} />
            <div className="certificate-authority"><small>{certificate.sealLabel}</small><strong>{rarityLabel(certificate)}</strong></div>
          </header>

          <section className="certificate-title-block">
            <p>{certificate.description}</p>
            <h1>{certificate.title}</h1>
            <span>{certificate.subtitle}</span>
          </section>

          <section className="certificate-recipient">
            <small>수료자</small>
            <h2>{state.child.nickname}</h2>
            <i aria-hidden="true" />
            <p>{certificate.achievementCopy}</p>
          </section>

          <section className="certificate-achievement-grid" aria-label="수료 시점 성장 기록">
            {achievementStats.map((item) => <div key={item.label}><img src={item.icon} alt="" /><span>{item.label}</span><strong>{item.value}</strong></div>)}
          </section>

          <section className="certificate-canon-quote">
            <span>MRM 성장 원칙 · {certificate.category}</span>
            <blockquote>“돈의 크기보다 선택의 깊이가 스노우볼을 키웁니다.”</blockquote>
          </section>

          <footer className="certificate-footer">
            <div className="certificate-character certificate-character-haneul"><Character src={characterAsset(certificate.character)} alt={`${certificate.shortTitle}을 상징하는 캐릭터`} /></div>
            <div className="certificate-issue-info"><small>발급번호</small><strong>{claimedAt ? certificateCode : '발급 대기 중'}</strong><span>{claimedAt ? formatDateKo(issueDate) : certificate.unlockRule}</span></div>
            <div className="certificate-signature"><small>MY real Money 성장교육 프로그램</small><strong>NEXPA.AI</strong><span>OFFICIAL MISSION CERTIFICATE</span></div>
            <img className="certificate-seal" src={`${A}/certificates/mrm_cert_03_completionseal_v01.png`} alt="MY real Money 공식 수료 인증 도장" />
            <div className="certificate-character certificate-character-sage"><Character src={`${A}/characters/sage/sage_checklist_v1.png`} alt="수료 기록을 인증하는 설옹" /></div>
          </footer>
        </article>
      </section>

      <Card className="certificate-keep-note"><strong>{claimedAt ? '명예의 전당에 보관되었습니다' : '수료증 컬렉션 안내'}</strong><p>{claimedAt ? `${formatDateKo(claimedAt)}에 발급된 수료증입니다. 배지 보드와 연도 냥장고에서 다시 확인할 수 있어요.` : '조건을 달성하고 발급받으면 배지 보드에 수집되며, 같은 주제의 수료증 세 개를 모으면 특별 프레임이 열립니다.'}</p></Card>
      <div className="certificate-actions">
        {claimedAt ? <PrimaryButton onClick={() => window.print()}>인쇄·PDF로 저장</PrimaryButton> : unlocked ? <PrimaryButton onClick={() => void claimCertificate()}>수료증 발급받기</PrimaryButton> : <Link className="primary-button link-button" to="/child/missions">연계 미션 확인하기</Link>}
        <Link className="secondary-button" to="/child/hall-of-honor">명예의 전당으로 돌아가기</Link>
      </div>
    </AppFrame>
  );
}
